import { TableData, TableDataTypes, Toaster, ToasterType } from './types';
import { CAPTAIN_TICKET_DISPOSITIONS } from './constants';
import { TicketConversationDialogComponent } from './ticket-conversation-dialog.component';

export const TICKET_LINK_TD_CLASS = 'cursor-pointer whiteSpacePre table-link-cell';
export const TICKET_LINK_SPAN_CLASS = 'table-link-btn';
export const TICKET_DEFAULT_TD_CLASS = '';
export const TICKET_BOOKING_ID_QUERY_CHUNK_SIZE = 50;
export const TICKET_V2_DEFAULT_PAGE_SIZE = 30;
export const TICKET_V2_ALL_PAGES = 0;
export const TICKET_CONVERSATION_DIALOG_WIDTH = '720px';
export const TICKET_CONVERSATION_COMMENT_LIMIT = 100;
const RD_ORDER_ID_PATTERN = /^RD/i;

export function chunkList(items, size) {
  if (!Array.isArray(items) || !size || size < 1) {
    return [];
  }
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

export function mergeTicketLists(ticketLists) {
  return (ticketLists || []).reduce((all, tickets) => all.concat(tickets || []), []);
}

export function paginateTicketList(tickets, pageNumber, pageSize = TICKET_V2_DEFAULT_PAGE_SIZE) {
  const items = tickets || [];
  const size = pageSize > 0 ? pageSize : items.length;
  if (!size) {
    return [];
  }
  const page = pageNumber > 0 ? pageNumber : 1;
  const start = (page - 1) * size;
  return items.slice(start, start + size);
}

export function shouldFetchNextTicketPage(tickets, pageSize = TICKET_V2_DEFAULT_PAGE_SIZE) {
  return Array.isArray(tickets) && tickets.length >= pageSize;
}

export function buildMobileTicketQuery(mobile: string): string {
  return JSON.stringify({ mobile });
}

export function normalizeTicketDescription(descriptionText?: string): string | null {
  return descriptionText ? descriptionText.replace(/ +(?= )/g, '') : null;
}

export function createTicketLinkTableData(ticketId, redirectionUrl: string): TableData {
  return createTicketActionTableData(ticketId, () => {
    if (redirectionUrl) {
      window.open(redirectionUrl, '_blank', 'noopener,noreferrer');
    }
  });
}

export function createTicketActionTableData(label, onClick): TableData {
  return new TableData({
    data: label,
    type: TableDataTypes.DATA,
    tdClassName: TICKET_LINK_TD_CLASS,
    spanClassName: TICKET_LINK_SPAN_CLASS,
    stopHover: true,
    onClick,
  });
}

function normalizeTicketId(value): string {
  if (value == null) {
    return '';
  }
  const trimmed = String(value).trim();
  if (!trimmed || trimmed.toUpperCase() === 'NA') {
    return '';
  }
  return trimmed;
}

export function getTicketRdOrderId(ticket, fallbackOrderId?: string): string {
  const candidates = [
    ticket && ticket.rd_order_id,
    ticket && ticket.uniqueId,
    fallbackOrderId,
  ];
  for (let index = 0; index < candidates.length; index++) {
    const trimmed = normalizeTicketId(candidates[index]);
    if (isRdOrderId(trimmed)) {
      return trimmed;
    }
  }
  return '';
}

export function isRdOrderId(value): boolean {
  return !!value && RD_ORDER_ID_PATTERN.test(value);
}

export function getTicketBookingId(ticket): string {
  const bookingId = normalizeTicketId(ticket && ticket.booking_id);
  if (bookingId) {
    return bookingId;
  }
  const rdOrderId = normalizeTicketId(ticket && ticket.rd_order_id);
  if (rdOrderId && !isRdOrderId(rdOrderId)) {
    return rdOrderId;
  }
  return '';
}

export function createTicketOrderIdCells(ticket, fallbackRdOrderId?: string) {
  return {
    rdOrderId: createTicketTableData(getTicketRdOrderId(ticket, fallbackRdOrderId)),
    orderId: createTicketTableData(getTicketBookingId(ticket)),
  };
}

export function createTicketConversationCell(onClick): TableData {
  return createTicketActionTableData('View', onClick);
}

export function mapBookingIdToRdOrderId(orders) {
  const rdOrderIdByBookingId = {};
  (orders || []).forEach((order) => {
    if (order && order._id && order.uniqueId) {
      rdOrderIdByBookingId[order._id] = order.uniqueId;
    }
  });
  return rdOrderIdByBookingId;
}

export function collectBookingIds(orders) {
  return (orders || []).map((order) => order && order._id).filter(Boolean);
}

export function filterCaptainIssueTickets(tickets, allowedSubReasons) {
  return (tickets || []).filter((ticket) => isAllowedCaptainIssueSubReason(ticket && ticket.sub_reason918254, allowedSubReasons));
}

export function buildConversationDialogData(ticketId, response, formatDate?, userLabel = 'Captain') {
  return {
    title: ticketId ? `Conversation — ${ticketId}` : 'Conversation',
    messages: buildTicketConversation(response, formatDate, userLabel),
  };
}

export function openTicketConversation({
  ticketingService,
  dialog,
  toasterService,
  userId,
  ticketId,
  formatDate,
  userLabel = 'Captain',
}) {
  ticketingService.fetchTicketConversation(userId, ticketId).subscribe((response) => {
    dialog.open(TicketConversationDialogComponent, {
      width: TICKET_CONVERSATION_DIALOG_WIDTH,
      data: buildConversationDialogData(ticketId, response, formatDate, userLabel),
    });
  }, () => {
    toasterService.showToaster(new Toaster({
      type: ToasterType.WARNING,
      message: `Error while getting comments for ticket ${ticketId}`,
      delay: 3000,
    }));
  });
}

export function isAllowedCaptainIssueSubReason(subReason, allowedSubReasons): boolean {
  return !!subReason && Array.isArray(allowedSubReasons) && allowedSubReasons.includes(subReason);
}

export function isCaptainProfileTicket(ticket): boolean {
  return ticket.ticket_source === 'chatService' ||
    !!(ticket.l1_agent_action && CAPTAIN_TICKET_DISPOSITIONS.includes(ticket.l1_agent_action));
}

export function resolveTicketHoverData(hoverData, value) {
  if (hoverData != null) {
    return hoverData;
  }
  if (value != null) {
    return value;
  }
  return '';
}

export function createTicketTableData(
  value,
  className: string = TICKET_DEFAULT_TD_CLASS,
  type?,
  flag = false,
  hoverData?,
): TableData {
  return new TableData({
    data: value,
    type: type ? type : TableDataTypes.DATA,
    flag,
    hoverData: resolveTicketHoverData(hoverData, value),
    tdClassName: className,
  });
}

export function getTicketPaginationCount(pageNo: number, perPage: number, ticketsInPage: number): number {
  if (ticketsInPage >= perPage) {
    return (pageNo + 1) * perPage + 1;
  }
  return pageNo * perPage + ticketsInPage;
}

export function extractTicketComments(response): any[] {
  if (!response) {
    return [];
  }
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(response.data)) {
    return response.data;
  }
  if (response.comments && Array.isArray(response.comments.data)) {
    return response.comments.data;
  }
  if (Array.isArray(response.comments)) {
    return response.comments;
  }
  return [];
}

export function getConversationSpeaker(comment, userLabel = 'Captain'): string {
  if (!comment) {
    return 'Unknown';
  }
  if (comment.category === 2 || comment.private) {
    return 'Private note';
  }
  if (comment.is_agent === 1 || comment.category === 3) {
    return 'Agent';
  }
  return userLabel || 'Captain';
}

export function getConversationSide(speaker): string {
  if (speaker === 'Agent') {
    return 'agent';
  }
  if (speaker === 'Private note') {
    return 'note';
  }
  return 'captain';
}

export function buildTicketConversation(response, formatDate?, userLabel = 'Captain'): any[] {
  const formatTime = formatDate || ((value) => value);
  return extractTicketComments(response)
    .slice()
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    .map((comment) => {
      const speaker = getConversationSpeaker(comment, userLabel);
      return {
        speaker,
        side: getConversationSide(speaker),
        text: comment.body_text || comment.body || '',
        time: comment.created_at ? formatTime(comment.created_at) : '',
      };
    });
}
